import React, { useState } from "react";
import { DocumentType } from "../../__generated__/graphql";
import { DisplayableDocument } from "../../contexts/DocumentManager";
import DocumentEditorModal from "./DocumentEditorModal";
import { DocumentEditorProps } from "./DocumentEditor";
import { PickerOptionsMode, useDocumentUpload } from "../../hooks/documents";
import { Pressable } from "react-native";
import BaseField, { BaseFieldProps } from "../BaseField";
import { useTheme } from "../../hooks/use-theme";
import { useDebouncedState } from "../../hooks/state";

export type DocumentInputProps = Omit<BaseFieldProps, "component" | "value"> &
    Omit<DocumentEditorProps, "visible" | "onClose"> & {
        value?: DisplayableDocument;
        onSelect?: (selection: DisplayableDocument) => Promise<void> | void;
        documentType: DocumentType;
        modes?: Parameters<typeof useDocumentUpload>[0]["modes"];
        webUploadFn?: string;
        enableCropZoom?: boolean;
    };

const DocumentButton: React.FC<DocumentInputProps> = ({
    style,
    onSelect,
    disabled = false,
    label,
    testID,
    documentType,
    value,
    error,
    right,
    left,
    asListAction,
    modes,
    webUploadFn,
    fixedName,
    enableCropZoom = true,
    ...props
}) => {
    const { colors } = useTheme();
    const [newDocument, setNewDocument] = useState<DisplayableDocument>();
    const [isProcessing, setIsProcessing] = useState(false);

    const { documentDropdownProps, controller } = useDocumentUpload({
        documentType,
        isPublic: true,
        enableCropZoom: enableCropZoom,
        generateUniqueId: true, // SUPER IMPORTANT: we need to prefix the document with uuid4 generated ID
        modes: modes || [
            PickerOptionsMode.Camera,
            PickerOptionsMode.Library,
            PickerOptionsMode.Storage,
        ],
        onSelect: (x) => {
            setNewDocument(x);
        },
        webUploadFn,
        fixedTitle: fixedName,
    });

    const closeDocumentEditor = React.useCallback(() => {
        controller?.abort();
        setNewDocument(undefined);
    }, [controller]);

    const saveDocument = React.useCallback(
        async (document: DisplayableDocument) => {
            setIsProcessing(true);
            controller?.abort();
            await onSelect?.(document);
            setNewDocument(undefined);
            setIsProcessing(false);
        },
        [controller, onSelect],
    );

    const onRemove = React.useCallback(() => {
        setNewDocument(undefined);
    }, []);

    // we use this to debounce the modal opening, as we use another model in library + photo chooser
    // there is a bug that prevent the display of another modal if the first one is not FULLY closed
    const isModalVisible = useDebouncedState(!!newDocument, 500);

    return (
        <>
            <BaseField
                testID={testID}
                style={style}
                left={left}
                right={right}
                value={typeof value === "string" ? value : value?.title || ""}
                valueColor={typeof value === "string" ? colors.fieldLabel : undefined}
                disabled={disabled}
                label={label}
                error={error}
                inputStyle={{ padding: 0, margin: 0, left: 0 }}
                asListAction={asListAction}
                componentRef={documentDropdownProps.ref}
                component={<Pressable onPress={documentDropdownProps.onPress} />}
            />
            <DocumentEditorModal
                visible={isModalVisible}
                document={newDocument}
                onClose={closeDocumentEditor}
                onSave={async (d) => {
                    await saveDocument(d);
                }}
                onRemove={onRemove}
                processing={isProcessing}
                fixedName={fixedName}
                {...props}
            />
        </>
    );
};

export default DocumentButton;
